// Copyright 2025 Northern.tech AS
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DeleteOutline as DeleteOutlineIcon, GroupWork as GroupWorkIcon } from '@mui/icons-material';
import { Button, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import InfoText from '@northern.tech/common-ui/InfoText';
import LinedHeader from '@northern.tech/common-ui/LinedHeader';
import Loader from '@northern.tech/common-ui/Loader';
import { ATTRIBUTE_SCOPES, DEPLOYMENT_STATES, DEVICE_FILTERING_OPTIONS } from '@northern.tech/store/constants';
import {
  getDeploymentsByStatus as getDeploymentsByStatusSelector,
  getDeploymentsById,
  getDevicesById,
  getGroups,
  getIdAttribute,
  getIsEnterprise,
  getUserCapabilities
} from '@northern.tech/store/selectors';
import { getDeploymentsByStatus, getGroups as getGroupsThunk, getSingleDeployment, removeStaticGroup } from '@northern.tech/store/thunks';

import DeploymentsList from './DeploymentsList';
import { DeploymentDeviceCount, DeploymentProgress, DeploymentRelease } from './DeploymentItem';
import { DEFAULT_DYNAMIC_GROUP, DYNAMIC_GROUP_DEPLOYMENT_PREFIX } from './constants';

// how many active deployments to pull per status when collecting the standing dynamic deployments
const activeDeploymentsPageSize = 500;

const useStyles = makeStyles()(theme => ({
  group: { marginBottom: theme.spacing(4) },
  header: { display: 'flex', alignItems: 'center', columnGap: theme.spacing() },
  // keep the per-group delete trigger compact so it sits inline with the group name in the LinedHeader
  deleteButton: { padding: theme.spacing(0.5) },
  // the delete confirmation floats in a popover anchored to the trash, so it is fully legible and
  // never squeezes the header (the trash icon itself stays visible the whole time)
  confirmPopover: { padding: theme.spacing(2), maxWidth: 320 },
  confirmActions: { display: 'flex', justifyContent: 'flex-end', columnGap: theme.spacing(), marginTop: theme.spacing(2) },
  // The shared deployment-item grid is tuned for the standard column set; our list only has
  // Release / # devices / Up to date / Status (+ the View details & Abort controls). Scope an
  // explicit grid here so the Status column is wide enough for "N devices in progress" /
  // "Current phase: …" instead of wrapping onto three lines.
  list: {
    '& .deployment-item': {
      gridTemplateColumns: 'minmax(180px, 2fr) 90px 90px minmax(260px, 1.4fr) max-content max-content !important'
    }
  }
}));

// Up-to-date = devices that already run the deployed release: Mender reports them as `success`
// (installed now) or `already-installed` (were already on the target artifact).
export const DeploymentUpToDate = ({ deployment }) => {
  const { device_count = 0, max_devices = 0, statistics: { status = {} } = {} } = deployment;
  const upToDate = (status.success || 0) + (status['already-installed'] || 0);
  const total = Math.max(device_count, max_devices, 0);
  return (
    <div className="align-right">
      {upToDate}
      {total ? ` / ${total}` : ''}
    </div>
  );
};

const headers = [
  { title: 'Release', renderer: DeploymentRelease },
  { title: '# devices', class: 'align-right column-defined', renderer: DeploymentDeviceCount },
  { title: 'Up to date', class: 'align-right column-defined', renderer: DeploymentUpToDate },
  { title: 'Status', renderer: DeploymentProgress }
];

// Recover the target group of a standing dynamic deployment: primarily from its `dyngroup:<group>`
// name, falling back to the inline `system:group` predicate for deployments created outside this UI.
export const deriveDynamicGroupName = deployment => {
  const { name = '', filter } = deployment;
  if (name.startsWith(DYNAMIC_GROUP_DEPLOYMENT_PREFIX)) {
    return name.slice(DYNAMIC_GROUP_DEPLOYMENT_PREFIX.length);
  }
  const groupPredicate = (filter?.filters ?? []).find(
    ({ scope, key, attribute }) => scope === ATTRIBUTE_SCOPES.system && (key === 'group' || attribute === 'group')
  );
  if (groupPredicate) {
    return groupPredicate.operator === DEVICE_FILTERING_OPTIONS.$nin.key || groupPredicate.type === DEVICE_FILTERING_OPTIONS.$nin.key
      ? DEFAULT_DYNAMIC_GROUP
      : groupPredicate.value;
  }
  return DEFAULT_DYNAMIC_GROUP;
};

const isDynamicDeployment = deployment => !!deployment && (deployment.dynamic || deployment.name?.startsWith(DYNAMIC_GROUP_DEPLOYMENT_PREFIX));

export const DynamicGroups = ({ abort, createClick, openReport }) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { static: staticGroups = [] } = useSelector(getGroups);
  const deploymentsById = useSelector(getDeploymentsById);
  const { inprogress, pending } = useSelector(getDeploymentsByStatusSelector);
  const { canConfigure, canDeploy } = useSelector(getUserCapabilities);
  const devices = useSelector(getDevicesById);
  const idAttribute = useSelector(getIdAttribute);
  const isEnterprise = useSelector(getIsEnterprise);

  const [loading, setLoading] = useState(true);
  // group awaiting delete confirmation (+ the trash element the popover is anchored to), and the
  // set of groups currently being deleted
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [confirmAnchor, setConfirmAnchor] = useState(null);
  const [deletingGroups, setDeletingGroups] = useState([]);
  const detailsLoaded = useRef(new Set());

  // pull the native inventory groups + the active deployments (dynamic deployments are standing, so
  // they live in the inprogress/pending buckets). shouldSelect:false keeps the Active tab untouched.
  const refresh = useCallback(
    () =>
      Promise.all([
        dispatch(getGroupsThunk()),
        dispatch(getDeploymentsByStatus({ status: DEPLOYMENT_STATES.inprogress, page: 1, perPage: activeDeploymentsPageSize, shouldSelect: false })),
        dispatch(getDeploymentsByStatus({ status: DEPLOYMENT_STATES.pending, page: 1, perPage: activeDeploymentsPageSize, shouldSelect: false }))
      ]),
    [dispatch]
  );

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const dynamicDeployments = useMemo(() => {
    const ids = Array.from(new Set([...inprogress.deploymentIds, ...pending.deploymentIds]));
    return ids.map(id => deploymentsById[id]).filter(isDynamicDeployment);
  }, [inprogress.deploymentIds, pending.deploymentIds, deploymentsById]);

  // ensure per-device statistics + device_count are populated for each standing deployment
  useEffect(() => {
    dynamicDeployments.forEach(({ id }) => {
      if (!detailsLoaded.current.has(id)) {
        detailsLoaded.current.add(id);
        dispatch(getSingleDeployment(id));
      }
    });
  }, [dispatch, dynamicDeployments]);

  const groupsWithDeployments = useMemo(() => {
    const byGroup = dynamicDeployments.reduce((accu, deployment) => {
      const groupName = deriveDynamicGroupName(deployment);
      accu[groupName] = accu[groupName] ? [...accu[groupName], deployment] : [deployment];
      return accu;
    }, {});
    const namedGroups = staticGroups.map(({ name }) => name);
    // surface every native group + the synthetic Default, plus any group a deployment references
    const groupNames = Array.from(new Set([DEFAULT_DYNAMIC_GROUP, ...namedGroups, ...Object.keys(byGroup)]));
    return groupNames.map(name => ({ name, deployments: byGroup[name] || [] }));
  }, [dynamicDeployments, staticGroups]);

  // native inventory groups that can actually be removed server-side (Default is synthetic, and a
  // group surfaced only through its standing deployments has no native group to delete).
  const staticGroupNames = useMemo(() => new Set(staticGroups.map(({ name }) => name)), [staticGroups]);

  // Deleting a dynamic group means: abort its standing dynamic deployments, then drop the native
  // inventory group so its devices fall back to Default. The group then no longer has active
  // deployments nor a native group, so the refresh removes it from this view.
  const closeConfirm = () => {
    setConfirmGroup(null);
    setConfirmAnchor(null);
  };

  const onDeleteGroup = ({ name, deployments }) => {
    closeConfirm();
    setDeletingGroups(current => [...current, name]);
    // 1. abort every standing dynamic deployment of the group (reuses the Deployments abort thunk)
    return Promise.all(deployments.map(({ id }) => abort(id)))
      // 2. remove the whole native group so its members lose their `group` attribute (→ Default)
      .then(() => (staticGroupNames.has(name) ? dispatch(removeStaticGroup(name)) : Promise.resolve()))
      // 3. refresh so the now-inactive group disappears
      .then(refresh)
      .catch(console.log)
      .finally(() => setDeletingGroups(current => current.filter(group => group !== name)));
  };

  // The trash trigger stays in the header at all times (it never gets swapped out, so it can't
  // "disappear"); clicking it opens a popover anchored to it with a fully legible confirmation.
  const renderGroupControl = name => {
    // Default is synthetic (no native group) and must never be deletable
    if (name === DEFAULT_DYNAMIC_GROUP || !canDeploy) {
      return null;
    }
    if (deletingGroups.includes(name)) {
      return <Loader show small table style={{ marginLeft: 8 }} />;
    }
    return (
      <Tooltip title="Delete group" placement="top">
        <IconButton
          className={classes.deleteButton}
          onClick={e => {
            setConfirmAnchor(e.currentTarget);
            setConfirmGroup(name);
          }}
          size="small"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  if (loading) {
    return <Loader show />;
  }

  const listProps = { abort, canConfigure, canDeploy, componentClass: classes.list, devices, headers, idAttribute, isEnterprise, openReport, type: DEPLOYMENT_STATES.inprogress };
  return (
    <div className="fadeIn margin-top">
      {dynamicDeployments.length ? (
        groupsWithDeployments.map(({ name, deployments }) => (
          <div className={classes.group} key={name}>
            <LinedHeader
              className="margin-top margin-right"
              heading={
                <span className={classes.header}>
                  <GroupWorkIcon fontSize="small" />
                  {name}
                  {renderGroupControl(name)}
                </span>
              }
            />
            {deployments.length ? (
              <DeploymentsList {...listProps} count={deployments.length} items={deployments} page={1} pageSize={deployments.length || 1} />
            ) : (
              <InfoText className="margin-left-small">No standing dynamic deployment for this group.</InfoText>
            )}
          </div>
        ))
      ) : (
        <div className="dashboard-placeholder">
          <p>Standing dynamic deployments will appear here, grouped by their target device group.</p>
          {canDeploy && (
            <p>
              <a onClick={createClick}>Create a deployment</a> with the &quot;Dynamic (auto-enrol group)&quot; option to get started.
            </p>
          )}
          <GroupWorkIcon style={{ fill: '#e3e3e3', width: 111, height: 111 }} />
        </div>
      )}
      <Popover
        open={Boolean(confirmGroup && confirmAnchor)}
        anchorEl={confirmAnchor}
        onClose={closeConfirm}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={classes.confirmPopover}>
          <Typography variant="subtitle1" gutterBottom>
            Delete group &quot;{confirmGroup}&quot;?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This aborts the group&apos;s standing dynamic deployments and moves its devices back to Default.
          </Typography>
          <div className={classes.confirmActions}>
            <Button onClick={closeConfirm}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onDeleteGroup({ name: confirmGroup, deployments: groupsWithDeployments.find(g => g.name === confirmGroup)?.deployments || [] })}
            >
              Delete
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default DynamicGroups;
